import React from 'react';
import { IconButton, Box, Link, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';
import { useNavigate } from 'react-router-dom';

const PAGE_ID = 'm2_u2_x2_hydraulische_schaltungen';

const images = [
  { key: 'hk_hydraulisch3', src: '/assets/drawable/hk_hydraulisch3.PNG', alt: 'Hydraulische Schaltung 3', label: 'Schaltung 3' },
  { key: 'hk_hydraulische1', src: '/assets/drawable/hk_hydraulische1.PNG', alt: 'Hydraulische Schaltung 1', label: 'Schaltung 1' },
  { key: 'hk_hydraulisch2', src: '/assets/drawable/hk_hydraulisch2.PNG', alt: 'Hydraulische Schaltung 2', label: 'Schaltung 2' },
  { key: 'hk_hydraulisch_leg', src: '/assets/drawable/hk_hydraulisch_leg.PNG', alt: 'Hydraulik Legende', label: 'Legende' }
];

const textBlocks = [
  {
    id: 'block_einleitung',
    heading: 'Hydraulische Schaltungen',
    body:
      'Eine hydraulische Schaltung ist die wasserseitige Verknüpfung verschiedener Komponenten einer Heizungsanlage, um einen funktionsfähigen Kreislauf zu bilden. ' +
      'Sie umfasst die Verbindungen zwischen dem Wärmeerzeuger (z.B. Kessel), der Wärmeverteilung (z.B. Heizkörper, Fußbodenheizung) und den Stellgliedern (z.B. Ventile, Pumpen). ' +
      'Die hydraulische Schaltung sorgt dafür, dass das Heizwasser richtig temperiert, gemischt und verteilt wird.'
  },
  {
    id: 'block_umfang',
    heading: 'Umfang dieser Schulung',
    body:
      'Die hydraulische Funktionsweise ist ein hoch anspruchsvoller Bereich, und es ist unmöglich, alle Arten und Schaltungen hier zu erklären. ' +
      'Deshalb begrenzen wir uns auf die, die uns von unserem Partner Belimo vorgelegt werden.'
  },
  {
    id: 'block_weiterfuehrend',
    heading: 'Weiterführende Informationen',
    body: 'Hier gibt es noch eine Homepage, auf der alles rund um Heizung genau erklärt wird. Insbesondere auf das Thema Hydraulik wird dort tief eingegangen.'
  }
];

const M2_U2_X2_HydraulischeSchaltungen: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM2, canUploadImagesM2 } = useAuth();
  const canEditTexts = canEditTextsM2;
  const canUploadImages = canUploadImagesM2;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const blocks = React.useMemo(() => {
    return textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] ?? tb.body
    }));
  }, [override]);

  const fullText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.body}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const imgSrc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const actions = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Vorlesen'
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '2.2.2 Hydraulische Schaltungen', actions },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      // Linke Spalte: Bilder
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      // Rechte Spalte: Textblöcke
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        blocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        // Link zu externer Ressource
        React.createElement(Box, { sx: { mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 } },
          React.createElement(Link, {
            href: 'https://www.haustechnikverstehen.de',
            target: '_blank',
            rel: 'noopener noreferrer',
            sx: { fontSize: '1.1rem', fontWeight: 500 }
          }, 'Heizung Allgemein – Haustechnik verstehen')
        ),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m2_u2_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M2_U2_X2_HydraulischeSchaltungen;

